/**
 * Intent-relevance predicate for coverage / evidence / progress percentages.
 *
 * PROBLEM
 * -------
 * Coverage and "% EVIDENCE" badges historically scored a site against the
 * UNION of every checklist requirement across all report types. A project
 * whose declared Project Intent only needs a SUBSET of that universe (e.g. a
 * dry-cooled, no-nuclear datacenter) was scored against requirements it will
 * never care about — so the percentage read misleadingly low.
 *
 * SOLUTION (this module — the single intent-relevance SSOT for coverage %)
 * ----------------------------------------------------------------------
 * Reuse the EXISTING intent SSOT — `providerImpactForIntent` (RV2) — to decide
 * whether a requirement counts toward the coverage DENOMINATOR (and, applied
 * consistently, the numerator). A requirement is intent-RELEVANT unless EVERY
 * SED section it strengthens is `irrelevant` for the intent. This is the exact
 * same `irrelevant`-tier semantics the Path-to-GO Evidence stage uses to
 * `suppressIrrelevant` rows in `SectionReadinessPanel`.
 *
 * EMPTY / UNKNOWN INTENT FALLBACK (no regression)
 * -----------------------------------------------
 * `providerImpactForIntent` fails SAFE: with a `null`/`undefined` intent — or
 * an "undecided" one — NO provider ever resolves to `irrelevant`
 * (waterStress demotes only to `minor`; nuclearInfra/adamsDocuments demote to
 * `minor`, not `irrelevant`, unless nuclear is EXPLICITLY rejected). So when
 * intent is unknown/empty every requirement stays relevant and the percentage
 * is computed against the FULL set exactly as before. The only requirements
 * ever dropped are nuclear-domain ones for a project that explicitly declared
 * `nuclearInterest: 'no'` — precisely the intent that should not be penalised
 * for missing nuclear data.
 *
 * PURE / DETERMINISTIC — same inputs always produce the same output. No I/O.
 */
import type { ChecklistItem } from '../../config/documentChecklists';
import type { ProjectIntent } from '../../types/siteRag';
import {
  providerImpactForIntent,
  type ProviderId,
} from '../providerImpactForIntent';

/**
 * The minimal requirement shape this predicate needs. Kept structural (not the
 * full `ChecklistItem`) so callers that only have `{ id, sedSectionsHelpful }`
 * — e.g. report-setup synthetic rows — can be scoped without a cast.
 */
export interface IntentScopableRequirement {
  /** SED sections this requirement strengthens. Each id is a `ProviderId`. */
  sedSectionsHelpful: ReadonlyArray<ProviderId>;
}

/**
 * True when the requirement should count toward an intent-scoped coverage /
 * evidence percentage.
 *
 * Relevance rule:
 *   - A requirement with NO `sedSectionsHelpful` mapping cannot be scoped
 *     against intent → it is ALWAYS relevant (conservative; never silently
 *     dropped from the denominator).
 *   - Otherwise it is relevant iff AT LEAST ONE of its SED sections is
 *     non-`irrelevant` for the intent. Only when EVERY section the
 *     requirement strengthens is `irrelevant` does the requirement drop out.
 *
 * When `intent` is null/undefined/undecided this returns `true` for every
 * requirement (see module header — `providerImpactForIntent` fails safe).
 */
export function isRequirementRelevantToIntent(
  requirement: IntentScopableRequirement,
  intent: ProjectIntent | null | undefined,
): boolean {
  const sections = requirement.sedSectionsHelpful;
  // No mapping → cannot scope; keep it in the denominator.
  if (!sections || sections.length === 0) return true;

  // Relevant if any strengthening section is not made irrelevant by the intent.
  for (const sectionId of sections) {
    const impact = providerImpactForIntent(sectionId, intent);
    if (impact.severity !== 'irrelevant') return true;
  }
  return false;
}

/**
 * Filter a requirement list down to the intent-relevant subset.
 *
 * This is the single denominator-scoping primitive every coverage-% surface
 * should funnel through so the report "N% EVIDENCE" badge (the field-coverage
 * ratio from `computeReportTypeFieldCoverage`) and the Site-Evidence
 * CoverageMatrix counts narrow consistently. The returned array preserves
 * input order.
 *
 * With an unknown/empty intent the input is returned unchanged (identity),
 * which is what guarantees the no-regression fallback.
 */
export function filterRequirementsByIntent<T extends IntentScopableRequirement>(
  requirements: ReadonlyArray<T>,
  intent: ProjectIntent | null | undefined,
): T[] {
  // Fast-path: nothing can be irrelevant under a null/undecided intent, so
  // avoid re-classifying every row. `isIntentScopable` captures the only two
  // intent dimensions `providerImpactForIntent` reads (cooling + nuclear).
  if (!isIntentScopable(intent)) return [...requirements];
  return requirements.filter(req => isRequirementRelevantToIntent(req, intent));
}

/**
 * Providers that `providerImpactForIntent` is CAPABLE of resolving to
 * `irrelevant` for some intent. Used only to drive the `isIntentScopable`
 * fast-path WITHOUT hardcoding which intent dimension does it — the SSOT
 * (`providerImpactForIntent`) stays authoritative. A parity test asserts this
 * list is complete (no other provider ever returns `irrelevant`), so if the
 * matrix grows a new irrelevant-capable provider the test fails until this is
 * extended.
 */
const IRRELEVANT_CAPABLE_PROVIDERS: readonly ProviderId[] = [
  'nuclearInfra',
  'adamsDocuments',
];

/**
 * True when the intent carries enough signal to make ANY requirement
 * irrelevant — i.e. at least one provider resolves to `irrelevant` under it.
 * Derived from the SSOT (`providerImpactForIntent`) rather than re-deciding
 * which intent field matters, so it can never drift from the matrix.
 *
 * Today this is satisfied only by an EXPLICIT `nuclearInterest: 'no'` (nuclear
 * domain → irrelevant). Air cooling never makes a domain irrelevant (water
 * demotes to `minor`). A null/undecided intent is never scopable → the
 * full-set fallback.
 *
 * Exported so callers can branch (e.g. show an "intent-scoped" affordance) and
 * so the fast-path in `filterRequirementsByIntent` lives in one place.
 */
export function isIntentScopable(intent: ProjectIntent | null | undefined): boolean {
  if (!intent) return false;
  return IRRELEVANT_CAPABLE_PROVIDERS.some(
    p => providerImpactForIntent(p, intent).severity === 'irrelevant',
  );
}
